import type { SourceErrorCode, SourceStatusDetail, VideoFrameSource } from '../core/definitions';
import type { SettingsBag } from '../core/model';
import { BaseSourceRuntime } from './base';
import { ElementFrameObserver, FrameCounter, type FrameStats } from './frameStats';
import { PAUSE_RETRY_WINDOW_MS, TrackWatchdog } from './liveness';
import { getLiveTrackIngestion, onIngestionChange, processorSupported, TrackFrameReader } from './trackIngest';

export interface PresentOptions {
  video: boolean;
  audio: boolean;
  /**
   * Remote WebRTC audio only flows through Web Audio once the stream is also
   * attached to a media element, so remote sources attach a muted <audio>.
   */
  activateAudioElement?: boolean;
  /** Resolve only after the first video frame size is known (default). Remote guests may never send one. */
  waitForVideo?: boolean;
}

interface Fault {
  code: SourceErrorCode;
  message: string;
}

export function stopTracks(stream: MediaStream | null): void {
  stream?.getTracks().forEach((t) => t.stop());
}

/** Calls `onEnded` when every given track has ended. Returns a remover. */
export function watchEnded(tracks: MediaStreamTrack[], onEnded: () => void): () => void {
  if (tracks.length === 0) return () => {};
  const handler = () => {
    if (tracks.every((t) => t.readyState === 'ended')) onEnded();
  };
  tracks.forEach((t) => t.addEventListener('ended', handler));
  return () => tracks.forEach((t) => t.removeEventListener('ended', handler));
}

/**
 * Base for sources that render a MediaStream: a muted <video> (or, when enabled, VideoFrames read
 * straight from the track) for the picture and a MediaStreamAudioSourceNode for sound. Ownership of
 * the stream's tracks stays with the subclass; this class only owns the element, reader and node.
 */
export abstract class StreamRuntime<S extends SettingsBag = SettingsBag> extends BaseSourceRuntime<S> {
  private element: HTMLVideoElement | null = null;
  private audioProbe: HTMLAudioElement | null = null;
  private audioNode: MediaStreamAudioSourceNode | null = null;
  private detachElement: (() => void) | null = null;
  private frameObserver: ElementFrameObserver | null = null;
  private reader: TrackFrameReader | null = null;
  private frame: VideoFrame | null = null;
  private readonly counter = new FrameCounter();
  private presented: { stream: MediaStream; opts: PresentOptions } | null = null;
  private watchdog: TrackWatchdog | null = null;
  private readonly faults = new Map<string, Fault>();
  private faultActive = false;
  private readonly unsubscribeIngestion = onIngestionChange(() => this.reingest());

  override getFrameStats(): FrameStats {
    this.frameObserver?.poll();
    return this.counter.stats();
  }

  override destroy(): void {
    this.unsubscribeIngestion();
    super.destroy();
  }

  /**
   * Reports a capture track that stays muted past the grace period as an error and clears it again
   * on unmute. Call after the stream is live; stopped by `stopWatching()`.
   */
  protected watchLiveness(tracks: MediaStreamTrack[], fault: Fault): void {
    this.stopWatching();
    if (tracks.length === 0) return;
    this.watchdog = new TrackWatchdog(tracks, {
      onStall: () => this.setFault('track', fault),
      onRecover: () => this.setFault('track', null),
    });
  }

  protected stopWatching(): void {
    this.watchdog?.dispose();
    this.watchdog = null;
    this.faults.clear();
    this.faultActive = false;
  }

  /** The stream's tracks ended: no further liveness reporting, status becomes 'ended'. */
  protected markEnded(detail?: SourceStatusDetail): void {
    this.stopWatching();
    this.setStatus('ended', undefined, detail);
  }

  private setFault(key: string, fault: Fault | null): void {
    if (this.destroyed) return;
    if (fault) this.faults.set(key, fault);
    else this.faults.delete(key);
    const first = this.faults.values().next().value as Fault | undefined;
    if (first) {
      this.faultActive = true;
      this.setStatus('error', first);
    } else if (this.faultActive) {
      this.faultActive = false;
      if (this.getStatus().state === 'error') this.setStatus('live');
    }
  }

  /** Resolves true once output is presented; false if superseded in the meantime. */
  protected async present(stream: MediaStream, epoch: number, opts: PresentOptions): Promise<boolean> {
    this.clearOutput();
    this.presented = null;
    const videoTracks = opts.video ? stream.getVideoTracks() : [];
    const audioTracks = opts.audio ? stream.getAudioTracks() : [];

    // Audio is wired first and independently: a video track that never delivers a frame (a guest
    // with only a microphone) must not keep the sound from reaching the mixer.
    if (audioTracks.length > 0) {
      const audioStream = new MediaStream(audioTracks);
      if (opts.activateAudioElement) {
        const probe = document.createElement('audio');
        probe.muted = true;
        probe.srcObject = audioStream;
        void probe.play().catch(() => {});
        this.audioProbe = probe;
      }
      this.audioNode = this.ctx.audioContext.createMediaStreamSource(audioStream);
      this.setAudio(this.audioNode);
    }

    if (videoTracks.length > 0) {
      let ingested: boolean | 'unsupported' = 'unsupported';
      if (getLiveTrackIngestion() === 'processor' && processorSupported()) {
        ingested = await this.presentFrames(videoTracks[0], epoch, opts);
      }
      if (ingested === false) return false;
      if (ingested === 'unsupported' && !(await this.presentElement(videoTracks, epoch, opts))) return false;
    }
    if (!this.isCurrent(epoch)) return false;
    this.presented = { stream, opts };
    return true;
  }

  private async presentElement(videoTracks: MediaStreamTrack[], epoch: number, opts: PresentOptions): Promise<boolean> {
    const el = document.createElement('video');
    el.muted = true;
    el.playsInline = true;
    el.autoplay = true;
    el.srcObject = new MediaStream(videoTracks);
    const sync = () => {
      if (this.element !== el) return;
      if (el.videoWidth > 0 && el.videoHeight > 0) {
        const cur = this.getVideo();
        if (!cur || cur.width !== el.videoWidth || cur.height !== el.videoHeight) {
          this.publishVideo(el, el.videoWidth, el.videoHeight, true);
        }
      } else {
        this.clearVideo();
      }
    };
    // The browser (not us) pausing a live element, e.g. a background-tab optimisation: resume once, then report.
    let lastPauseAt = -Infinity;
    let retried = false;
    const unresumable: Fault = { code: 'unknown', message: 'The browser paused the video and it could not be resumed' };
    const onPause = () => {
      if (this.element !== el || this.destroyed) return;
      const now = performance.now();
      if (retried && now - lastPauseAt < PAUSE_RETRY_WINDOW_MS) {
        this.setFault('element', unresumable);
        return;
      }
      retried = true;
      lastPauseAt = now;
      el.play().catch(() => this.setFault('element', unresumable));
    };
    const onPlaying = () => {
      if (this.element === el) this.setFault('element', null);
    };
    el.addEventListener('resize', sync);
    el.addEventListener('loadedmetadata', sync);
    el.addEventListener('pause', onPause);
    el.addEventListener('playing', onPlaying);
    const observer = new ElementFrameObserver(el, this.counter);
    observer.start();
    this.element = el;
    this.frameObserver = observer;
    this.detachElement = () => {
      el.removeEventListener('resize', sync);
      el.removeEventListener('loadedmetadata', sync);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('playing', onPlaying);
      observer.stop();
    };
    // Only the element created here may be torn down on supersession; a newer present() owns the shared fields.
    const superseded = () => {
      if (this.element === el) this.clearOutput();
      return false;
    };
    el.play().catch((err) => this.log('warn', 'video play() rejected', err));
    if (opts.waitForVideo !== false) {
      await new Promise<void>((resolve, reject) => {
        if (el.readyState >= HTMLMediaElement.HAVE_METADATA) return resolve();
        el.addEventListener('loadedmetadata', () => resolve(), { once: true });
        el.addEventListener('error', () => reject(new Error('Video element failed to start')), { once: true });
      });
      if (!this.isCurrent(epoch)) return superseded();
    }
    sync();
    return true;
  }

  /** Reads VideoFrames from the track. Resolves 'unsupported' when the processor cannot be created. */
  private async presentFrames(track: MediaStreamTrack, epoch: number, opts: PresentOptions): Promise<boolean | 'unsupported'> {
    let firstFrame: () => void = () => {};
    const first = new Promise<void>((resolve) => (firstFrame = resolve));
    const reader = new TrackFrameReader(track, {
      onFrame: (frame) => {
        if (this.reader !== reader || this.destroyed) {
          frame.close();
          return;
        }
        const previous = this.frame;
        this.frame = frame;
        this.counter.note();
        // The renderer reads provider.source at each upload and never keeps or closes the frame.
        const source: VideoFrameSource = frame;
        const cur = this.getVideo();
        if (cur && cur.width === frame.displayWidth && cur.height === frame.displayHeight) this.replaceSource(source);
        else this.publishVideo(source, frame.displayWidth, frame.displayHeight, false);
        previous?.close();
        firstFrame();
      },
      onClose: () => {
        if (this.reader === reader) firstFrame();
      },
    });
    this.reader = reader;
    try {
      reader.start();
    } catch (err) {
      this.reader = null;
      this.log('info', 'MediaStreamTrackProcessor unavailable for this track; using the video element', err);
      return 'unsupported';
    }
    if (opts.waitForVideo !== false) {
      await first;
      if (!this.isCurrent(epoch)) {
        if (this.reader === reader) this.clearOutput();
        return false;
      }
    }
    return true;
  }

  /** Re-presents the current stream after the ingestion mode changed. */
  private reingest(): void {
    const presented = this.presented;
    if (!presented || this.destroyed) return;
    const epoch = this.beginEpoch();
    this.present(presented.stream, epoch, presented.opts).catch((err) => this.log('warn', 'switching ingestion failed', err));
  }

  /** Detaches element, reader and node without stopping the stream's tracks. */
  protected clearOutput(): void {
    this.detachElement?.();
    this.detachElement = null;
    this.frameObserver = null;
    this.setFault('element', null);
    if (this.element) {
      this.element.pause();
      this.element.srcObject = null;
      this.element = null;
    }
    const reader = this.reader;
    this.reader = null;
    reader?.stop();
    this.frame?.close();
    this.frame = null;
    if (this.audioProbe) {
      this.audioProbe.pause();
      this.audioProbe.srcObject = null;
      this.audioProbe = null;
    }
    if (this.audioNode) {
      this.audioNode.disconnect();
      this.audioNode = null;
    }
    this.setAudio(null);
    this.clearVideo();
  }
}
